import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, MapPin, Gift, Music, Utensils, Check, ExternalLink } from "lucide-react";
import { Toaster } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const public_api = axios.create({ baseURL: `${BASE}/api` });

export default function PublicRSVP() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("token") || null;
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [guest, setGuest] = useState(null);
  const [guestToken, setGuestToken] = useState(inviteToken);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    rsvp_status: "yes", meal_choice: "", song_request: "", plus_one_name: "", notes: "", party_size: 1,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await public_api.get(`/public/events/${eventId}`);
        setEvent(data);
      } catch (e) {
        setError(e.response?.status === 403 ? "RSVP for this event is closed." : "Event not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  // If the invite link came with ?token=..., look up the guest directly and skip the name search.
  useEffect(() => {
    if (!inviteToken) return;
    (async () => {
      try {
        const { data } = await public_api.get(`/public/events/${eventId}/guests/by-token`, { params: { token: inviteToken } });
        setGuest(data);
        setGuestToken(inviteToken);
        setForm(f => ({
          ...f,
          rsvp_status: data.rsvp_status === "pending" ? "yes" : data.rsvp_status,
          meal_choice: data.meal_choice || "",
          song_request: data.song_request || "",
          plus_one_name: data.plus_one_name || "",
          party_size: data.party_size || 1,
        }));
      } catch (e) { /* invalid token — fall through to name lookup */ console.debug("rsvp: token lookup failed, falling back to name search", e); }
    })();
  }, [eventId, inviteToken]);

  const search = async () => {
    if (query.trim().length < 2) { toast.error("Type at least 2 characters"); return; }
    setSearching(true);
    try {
      const { data } = await public_api.get(`/public/events/${eventId}/guests/lookup`, { params: { q: query } });
      setResults(data);
      if (data.length === 0) toast.error("We couldn't find your name. Please check spelling or ask your host.");
    } catch (e) { toast.error("Something went wrong"); }
    finally { setSearching(false); }
  };

  const chooseGuest = (g) => {
    setGuest(g);
    setGuestToken(g.rsvp_token || null);
    setForm({
      rsvp_status: g.rsvp_status === "pending" ? "yes" : g.rsvp_status,
      meal_choice: g.meal_choice || "",
      song_request: g.song_request || "",
      plus_one_name: g.plus_one_name || "",
      notes: "",
      party_size: g.party_size || 1,
    });
    setResults([]);
    setQuery("");
  };

  const submit = async () => {
    if (!guestToken) {
      toast.error("This RSVP link is missing a security token. Please use the link from your invite email.");
      return;
    }
    try {
      await public_api.post(`/public/events/${eventId}/rsvp/${guest.id}?token=${encodeURIComponent(guestToken)}`, {
        ...form,
        party_size: Number(form.party_size) || 1,
      });
      setSubmitted(true);
      toast.success("Your RSVP is in — thank you!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit RSVP");
    }
  };

  if (loading) {
    return <div className="theme-light min-h-screen flex items-center justify-center bg-[#F7F7F8]"><div className="eyebrow text-[#71717A]">Loading…</div></div>;
  }
  if (error || !event) {
    return (
      <div className="theme-light min-h-screen flex items-center justify-center bg-[#F7F7F8] p-6">
        <div className="text-center max-w-md">
          <div className="eyebrow mb-3">Not available</div>
          <div className="font-serif text-3xl text-[#0A0A0A]">{error || "This RSVP page is unavailable."}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-light min-h-screen bg-[#F7F7F8]">
      <Toaster position="top-center" richColors />

      {/* Hero */}
      <div className="relative h-72 md:h-96 overflow-hidden">
        {event.cover_image ? (
          <img src={event.cover_image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[#0A0A0A]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/70" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <div className="text-white max-w-2xl">
            <div className="eyebrow text-white/80 mb-3">You&apos;re invited to</div>
            <h1 className="font-serif text-5xl md:text-7xl leading-none tracking-tight" data-testid="rsvp-event-title">{event.title}</h1>
            {event.couple_names && <div className="font-serif text-xl md:text-2xl text-white/90 mt-4 italic">{event.couple_names}</div>}
            <div className="mt-6 flex items-center justify-center gap-6 text-sm font-light flex-wrap">
              <span className="flex items-center gap-2"><CalendarDays size={14} strokeWidth={1.5} />{new Date(event.event_date).toLocaleDateString(undefined, { weekday:"long", year:"numeric", month:"long", day:"numeric" })}</span>
              {event.venue && <span className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.5} />{event.venue}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16 space-y-14">
        {/* Welcome message */}
        {event.rsvp_message && (
          <section>
            <div className="eyebrow mb-3 text-center">A note from your hosts</div>
            <p className="font-serif text-2xl md:text-3xl text-[#0A0A0A] text-center leading-snug">{event.rsvp_message}</p>
          </section>
        )}

        {/* RSVP section */}
        {!submitted ? (
          <section className="bg-white border border-[#E4E4E7] rounded-sm p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="eyebrow mb-2">Kindly respond</div>
              <h2 className="font-serif text-3xl text-[#0A0A0A]">Let us know you&apos;re coming</h2>
            </div>

            {!guest ? (
              <div>
                <Label className="eyebrow block mb-2">Find your invitation</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="rsvp-lookup-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Type your first or last name"
                    className="rounded-sm"
                  />
                  <Button
                    data-testid="rsvp-lookup-btn"
                    onClick={search}
                    disabled={searching}
                    className="rounded-sm bg-[#0A0A0A] hover:bg-[#000000] shrink-0"
                  >
                    {searching ? "Searching…" : "Find me"}
                  </Button>
                </div>

                {results.length > 0 && (
                  <div className="mt-4 border-t border-[#E4E4E7] pt-4 space-y-1">
                    <div className="eyebrow mb-2">Is that you?</div>
                    {results.map(g => (
                      <button
                        key={g.id}
                        onClick={() => chooseGuest(g)}
                        data-testid={`rsvp-select-${g.id}`}
                        className="w-full text-left px-4 py-3 border border-[#E4E4E7] rounded-sm hover:bg-[#F7F7F8] hover:border-[#0A0A0A] transition-colors"
                      >
                        <div className="text-[#111113]">{g.name}</div>
                        <div className="text-xs text-[#71717A] font-light">Party of {g.party_size}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-5">
                <div className="text-center pb-4 border-b border-[#E4E4E7]">
                  <div className="eyebrow">Hello,</div>
                  <div className="font-serif text-3xl text-[#0A0A0A]" data-testid="rsvp-guest-name">{guest.name}</div>
                  <button onClick={() => setGuest(null)} className="text-xs text-[#71717A] hover:text-[#0A0A0A] mt-2 underline">Not you?</button>
                </div>

                <div>
                  <Label className="eyebrow block mb-3">Will you attend?</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["yes","Joyfully"], ["no","Regretfully decline"], ["maybe","Undecided"]].map(([v,l]) => (
                      <button
                        key={v}
                        onClick={() => setForm({ ...form, rsvp_status: v })}
                        data-testid={`rsvp-status-${v}`}
                        className={`px-3 py-3 rounded-sm border text-sm transition-colors ${
                          form.rsvp_status === v
                            ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                            : "bg-white text-[#111113] border-[#E4E4E7] hover:border-[#0A0A0A]"
                        }`}
                      >{l}</button>
                    ))}
                  </div>
                </div>

                {form.rsvp_status !== "no" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="eyebrow block mb-2 flex items-center gap-2"><Utensils size={12} strokeWidth={1.5}/> Meal choice</Label>
                        <Input
                          data-testid="rsvp-meal-input"
                          value={form.meal_choice}
                          onChange={(e) => setForm({ ...form, meal_choice: e.target.value })}
                          placeholder="e.g. Vegetarian"
                          className="rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="eyebrow block mb-2">Party size</Label>
                        <Input
                          type="number" min={1} max={guest.party_size || 10}
                          value={form.party_size}
                          onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                          className="rounded-sm"
                        />
                        <div className="text-xs text-[#71717A] mt-1 font-light">You&apos;re allotted {guest.party_size}</div>
                      </div>
                    </div>

                    {guest.party_size > 1 && (
                      <div>
                        <Label className="eyebrow block mb-2">Guest / plus-one name</Label>
                        <Input
                          value={form.plus_one_name}
                          onChange={(e) => setForm({ ...form, plus_one_name: e.target.value })}
                          className="rounded-sm"
                        />
                      </div>
                    )}

                    <div>
                      <Label className="eyebrow block mb-2 flex items-center gap-2"><Music size={12} strokeWidth={1.5}/> Song request</Label>
                      <Input
                        data-testid="rsvp-song-input"
                        value={form.song_request}
                        onChange={(e) => setForm({ ...form, song_request: e.target.value })}
                        placeholder="What'll get you on the dance floor?"
                        className="rounded-sm"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label className="eyebrow block mb-2">A note to your hosts (optional)</Label>
                  <Textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="rounded-sm"
                  />
                </div>

                <Button
                  onClick={submit}
                  data-testid="rsvp-submit-btn"
                  className="w-full rounded-sm bg-[#0A0A0A] hover:bg-[#000000] h-12"
                >
                  <Check size={16} strokeWidth={1.5} className="mr-2" /> Send my RSVP
                </Button>
              </div>
            )}
          </section>
        ) : (
          <section className="bg-white border border-[#E4E4E7] rounded-sm p-10 text-center" data-testid="rsvp-confirmation">
            <div className="w-16 h-16 rounded-full bg-[#F4F4F5] mx-auto flex items-center justify-center mb-4">
              <Check size={28} strokeWidth={1.5} className="text-[#0A0A0A]" />
            </div>
            <div className="eyebrow mb-2">Received</div>
            <h2 className="font-serif text-3xl text-[#0A0A0A] mb-3">Thank you, {guest.name}</h2>
            <p className="text-[#71717A] font-light">
              {form.rsvp_status === "yes" && "We can't wait to celebrate with you."}
              {form.rsvp_status === "no" && "You'll be missed. Thank you for letting us know."}
              {form.rsvp_status === "maybe" && "We hope you can make it — feel free to update your RSVP anytime."}
            </p>
            <button onClick={() => { setSubmitted(false); setGuest(null); }} className="text-sm text-[#71717A] hover:text-[#0A0A0A] mt-6 underline">Update my response</button>
          </section>
        )}

        {/* Registry */}
        {event.registry && event.registry.length > 0 && (
          <section>
            <div className="text-center mb-6">
              <div className="eyebrow mb-2">Your presence is a present</div>
              <h2 className="font-serif text-3xl text-[#0A0A0A] flex items-center justify-center gap-3">
                <Gift size={22} strokeWidth={1.5} /> Registry
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {event.registry.map(r => (
                <a
                  key={r.id}
                  href={r.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`registry-link-${r.id}`}
                  className="bg-white border border-[#E4E4E7] rounded-sm p-5 card-lift flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    {r.retailer && <div className="eyebrow mb-1">{r.retailer}</div>}
                    <div className="font-serif text-xl text-[#0A0A0A] truncate">{r.title}</div>
                    {r.notes && <div className="text-sm text-[#71717A] font-light mt-1">{r.notes}</div>}
                  </div>
                  <ExternalLink size={16} strokeWidth={1.5} className="text-[#71717A] shrink-0" />
                </a>
              ))}
            </div>
          </section>
        )}

        <footer className="text-center pt-8 border-t border-[#E4E4E7]">
          <div className="eyebrow text-[#71717A]">Curated with care · AtelisOS</div>
        </footer>
      </div>
    </div>
  );
}
